import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { CamelCase } from '../../modules/CamelCase.js';
import { logger } from '@sentry/browser';

/**
 * Determines whether a conversation shows in a specific user's inbox.
 * @param {{queued_for?: string[], updated_by?: string|null}} conversation
 * @param {string} userId
 * @returns {boolean} True when the conversation is queued for the user and they did not send the latest message.
 */
export function checkConversationIsInUserInbox(conversation, userId) {
  // Leave out conversations the user sent the latest message in
  const isQueuedForUser = conversation?.queued_for?.includes(userId) ?? false;
  const isUserTheLastSender = conversation?.updated_by === userId;
  return isQueuedForUser && !isUserTheLastSender;
}

/**
 * Moves a conversation to a specific user's inbox.
 * @param {firebase.firestore.DocumentReference} conversationRef
 * @param {string} userId
 */
export async function moveConversationToUserInbox(conversationRef, userId) {
  if (!conversationRef || !userId) throw new Error('Missing required parameters');
  await updateDoc(conversationRef, { queued_for: arrayUnion(userId) });

  // Track moving conversations to inbox
  logger.info('Moved feedback conversation to user inbox');
}

/**
 * Removes a conversation from a specific user's inbox.
 * @param {firebase.firestore.DocumentReference} conversationRef
 * @param {string} userId
 */
export async function removeConversationFromUserInbox(conversationRef, userId) {
  if (!conversationRef || !userId) throw new Error('Missing required parameters');
  await updateDoc(conversationRef, { queued_for: arrayRemove(userId) });

  // Track removing conversations from inbox
  logger.info('Removed feedback conversation from user inbox');
}

/**
 * Marks a conversation as unread for a specific user.
 * @param {firebase.firestore.DocumentReference} conversationRef
 * @param {string} userId
 */
export async function markConversationAsUnreadForUser(conversationRef, userId) {
  if (!conversationRef || !userId) throw new Error('Missing required parameters');
  await updateDoc(conversationRef, { unread_by: arrayUnion(userId) });

  // Track marking conversations as unread
  logger.info('Marked feedback conversation as unread');
}

/**
 * Create a new conversation in the feedback collection.
 * @param {Object} entityData - The data for the new conversation.
 * @param {string} userId - The ID of the user creating the conversation.
 * @param {boolean} isPublic - Whether the conversation is public or private.
 * @return {Promise<firebase.firestore.DocumentReference>} - The reference to the newly created conversation.
 */
export async function createConversation(entityData, userId, isPublic) {
  const labels = await getFeedbackEntityLabels(entityData);
  let conversation = {
    // Blank conversation data
    started_by: userId,
    started_at: null,
    updated_by: null,
    updated_at: null,
    subscribers: [],
    unread_by: [],
    queued_for: [],
    issue_status: 'NONE',
    ...entityData,
    subject: labels.node ? `SCID ${labels.node} / ${labels.job}` : labels.job
  };
  // TODO (2025-05-09): Avoid creating empty conversations
  const feedbackPath = isPublic ? `jobs/${entityData.job_id}/public_feedback` : `companies/${entityData.company_id}/feedback`;
  const feedbackCollectionRef = firebase.firestore().collection(feedbackPath);
  const conversationDocRef = await feedbackCollectionRef.add(conversation);
  // Track conversation creation
  logger.info('Created new feedback conversation');
  return conversationDocRef;
}

/**
 * Get the labels for a feedback entity (job name, node name, etc...)
 * @param {object} entityData - The data for the entity.
 * @returns {Promise<object>} - The labels for the entity.
 */
export async function getFeedbackEntityLabels(entityData) {
  let labels = {};
  // Return empty labels if no entity data is provided
  if (!entityData) return labels;

  const jobRef = globalThis.FirebaseWorker.database().ref(`photoheight/jobs/${entityData.job_id}`);
  if (entityData.job_id) {
    labels.job = await jobRef
      .child('name')
      .once('value')
      .then((s) => s.val());
  }

  if (entityData.entity_type == 'NODE') {
    const attributeValue = await jobRef
      .child(`nodes/${entityData.entity_id}/attributes/scid`)
      .once('value')
      .then((s) => s.val());
    labels.node = attributeValue ? `SCID ${Object.values(attributeValue)[0]}` : entityData.entity_id;
  }

  if (entityData.subentity_type == 'ACTION') {
    const action = await firebase.firestore().doc(`companies/${entityData.company_id}/action_tracking/${entityData.subentity_id}`).get();
    if (action.exists) labels.action = action.data()?.action_name;
  }
  if (entityData.subentity_type == 'ATTRIBUTE_EVENT') {
    const path = `companies/${entityData.company_id}/projects/${entityData.project_id}/attribute_events_latest/${entityData.subentity_id}`;
    const event = await firebase.firestore().doc(path).get();
    if (event.exists) {
      labels.event_attribute = CamelCase(event.data()?.attribute);
      labels.event_instance = event.data()?.instance_id;
    }
  }

  return labels;
}
